import React, { useEffect, useState } from 'react'
import ReactDOM from 'react-dom/client'
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react'
import axios from 'axios'
import './index.css'
import App from './App'
import 'react-toastify/dist/ReactToastify.css'
import LoginPage from './Components/Auth/LoginPage'
import LoadingScreen from './Components/Auth/LoadingScreen'
import { APIDataUrl, APITokenUrl } from './Utils/Utils'

const domain = process.env.REACT_APP_AUTH0_DOMAIN
const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID

const AUTH0_NAMESPACE = 'https://orbit-intel.app'

// Looks up the Orbit numeric user id by email. Confirmed response shape: { user_id: number }
const fetchOrbitUserId = async (email) => {
  const option = {
    method: 'GET',
    headers: {
      'access-control-allow-origin': '*',
      'content-type': 'application/json',
      'X-API-Key': APITokenUrl(),
    },
    url: `${APIDataUrl()}/v1/get_user_id?email=${encodeURIComponent(email)}`,
  }
  const res = await axios(option)
  return res?.data?.user_id ?? null
}

function AuthGate() {
  const { isLoading, isAuthenticated, user, error, loginWithRedirect, logout } = useAuth0()
  const [userLookupDone, setUserLookupDone] = useState(false)

  // Resolve the Orbit user id from the backend, then seed localStorage so
  // existing app components (which read userId/email/auth) keep working.
  useEffect(() => {
    if (!isAuthenticated || !user) return

    const orbitEmail = user[`${AUTH0_NAMESPACE}/orbit_email`] || user.email
    console.log('[Auth0] Google email:', user.email)
    console.log('[Auth0] Orbit email:', user[`${AUTH0_NAMESPACE}/orbit_email`] ?? 'none')

    const firstName = user.given_name || user.name?.split(' ')[0] || ''
    const lastName  = user.family_name || user.name?.split(' ').slice(1).join(' ') || ''

    fetchOrbitUserId(orbitEmail)
      .then((userId) => {
        if (userId != null) {
          localStorage.setItem('userId', JSON.stringify(Number(userId)))
        }
        localStorage.setItem('email', orbitEmail)
        localStorage.setItem('auth', JSON.stringify({
          user: {
            profile: {
              login: orbitEmail,
              email: orbitEmail,
              username: orbitEmail,
              firstName,
              lastName,
            },
          },
          isAuthenticated: true,
        }))
      })
      .catch((err) => {
        console.error('[Auth0] Failed to resolve user id from /v1/get_user_id', err)
      })
      .finally(() => setUserLookupDone(true))
  }, [isAuthenticated, user])

  const handleTryDifferentAccount = () => {
    logout({ logoutParams: { returnTo: window.location.origin } })
  }

  if (isLoading) return <LoadingScreen />
  if (isAuthenticated && !userLookupDone) return <LoadingScreen />

  if (error) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: '16px', background: '#001A54', color: '#fff', fontFamily: 'sans-serif', textAlign: 'center', padding: '0 24px' }}>
        <p style={{ fontSize: '20px', fontWeight: 600, margin: 0 }}>Access denied</p>
        <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)', margin: 0, maxWidth: '360px' }}>{error.message}</p>
        <button
          onClick={handleTryDifferentAccount}
          style={{ marginTop: '8px', padding: '12px 24px', background: '#1976D2', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}
        >
          Try a different account
        </button>
      </div>
    )
  }

  if (!isAuthenticated) return <LoginPage />

  return <App user={user} />
}

const root = ReactDOM.createRoot(document.getElementById('root'))
root.render(
  <React.StrictMode>
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{ redirect_uri: window.location.origin, scope: 'openid profile email offline_access' }}
      useRefreshTokens={true}
      cacheLocation="localstorage"
    >
      <AuthGate />
    </Auth0Provider>
  </React.StrictMode>
)
